/**
 * Properties List Page
 * View and manage all properties across all landlords
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Progress,
  useColorModeValue,
  Avatar,
  Tooltip,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdVisibility,
  MdLocationOn,
  MdBusiness,
  MdTrendingUp,
  MdWarning,
  MdCheckCircle,
  MdDomain,
  MdPeople,
  MdAttachMoney,
} from 'react-icons/md';
import { Property, PropertyStats, PropertyFilters } from '../../types/property';
import { Lead } from '../../types/crm';
import { propertyService } from '../services/propertyService';

export function PropertiesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [properties, setProperties] = useState<Property[]>([]);
  const [stats, setStats] = useState<PropertyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<PropertyFilters>({
    search: '',
    status: 'all',
    type: 'all',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadProperties();
  }, [filters]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      const response = await propertyService.getProperties(filters);
      setProperties(response.properties);
      
      // Load stats separately
      const statsResponse = await propertyService.getPropertyStats();
      setStats(statsResponse.stats);
    } catch (error: any) {
      console.error('Error loading properties:', error);
      toast({
        title: 'Error loading properties',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data on error
      setProperties(mockProperties);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Property['status']) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'pending': return 'yellow';
      case 'archived': return 'red';
      default: return 'gray';
    }
  };

  const getOccupancyColor = (rate: number) => {
    if (rate >= 95) return 'green';
    if (rate >= 80) return 'yellow';
    return 'red';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const calculateOccupancyRate = (property: Property) => {
    if (property.totalUnits === 0) return 0;
    return (property.occupiedUnits / property.totalUnits) * 100;
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Properties</Heading>
          <Text color="gray.600" fontSize="sm">
            Manage all properties across all landlords
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadProperties}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdDomain} boxSize={8} color="blue.500" />
                  <Box>
                    <StatLabel>Total Properties</StatLabel>
                    <StatNumber>{stats.totalProperties}</StatNumber>
                    <StatHelpText>{stats.activeProperties} active</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdBusiness} boxSize={8} color="purple.500" />
                  <Box>
                    <StatLabel>Total Units</StatLabel>
                    <StatNumber>{stats.totalUnits}</StatNumber>
                    <StatHelpText>{stats.occupancyRate.toFixed(1)}% occupied</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdAttachMoney} boxSize={8} color="green.500" />
                  <Box>
                    <StatLabel>Monthly Revenue</StatLabel>
                    <StatNumber fontSize="lg">{formatCurrency(stats.totalMonthlyRevenue)}</StatNumber>
                    <StatHelpText>{stats.averageCollectionRate.toFixed(1)}% collected</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdPeople} boxSize={8} color="orange.500" />
                  <Box>
                    <StatLabel>Landlords</StatLabel>
                    <StatNumber>{stats.topLandlords.length}</StatNumber>
                    <StatHelpText>Managing properties</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search properties, landlords..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </InputGroup>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="archived">Archived</option>
            </Select>

            <Select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
            >
              <option value="all">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="mixed">Mixed Use</option>
            </Select>

            <Select
              placeholder="All Provinces"
              onChange={(e) => setFilters({ ...filters, province: e.target.value || undefined })}
            >
              <option value="">All Provinces</option>
              <option value="ON">Ontario</option>
              <option value="QC">Quebec</option>
              <option value="BC">British Columbia</option>
              <option value="AB">Alberta</option>
              <option value="MB">Manitoba</option>
              <option value="SK">Saskatchewan</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Properties Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Properties List</Heading>
            <Text fontSize="sm" color="gray.600">
              {properties.length} properties
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : properties.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdDomain} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No properties found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Property</Th>
                    <Th>Landlord</Th>
                    <Th>Location</Th>
                    <Th>Units</Th>
                    <Th>Occupancy</Th>
                    <Th>Monthly Rent</Th>
                    <Th>Collection Rate</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {properties.map((property) => {
                    const occupancyRate = calculateOccupancyRate(property);
                    return (
                      <Tr
                        key={property.id}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        onClick={() => navigate(`/admin/properties/${property.id}`)}
                      >
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{property.name}</Text>
                            <Badge colorScheme={property.type === 'residential' ? 'blue' : property.type === 'commercial' ? 'purple' : 'green'}>
                              {property.type}
                            </Badge>
                          </VStack>
                        </Td>
                        <Td>
                          <HStack>
                            <Avatar size="sm" name={property.landlordName} />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">{property.landlordName}</Text>
                              <Text fontSize="xs" color="gray.600">{property.landlordEmail}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <HStack>
                            <Icon as={MdLocationOn} color="gray.400" />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm">{property.address.city}</Text>
                              <Text fontSize="xs" color="gray.600">{property.address.province}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{property.totalUnits}</Text>
                            <Text fontSize="xs" color="gray.600">
                              {property.occupiedUnits} occupied
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color={getOccupancyColor(occupancyRate)}>
                              {occupancyRate.toFixed(1)}%
                            </Text>
                            <Progress
                              value={occupancyRate}
                              size="sm"
                              colorScheme={getOccupancyColor(occupancyRate)}
                              w="60px"
                              borderRadius="full"
                            />
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{formatCurrency(property.totalMonthlyRent)}</Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatCurrency(property.averageRentPerUnit)}/unit
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold" color={getOccupancyColor(property.collectionRate)}>
                              {property.collectionRate.toFixed(1)}%
                            </Text>
                            {property.outstandingBalance > 0 && (
                              <Text fontSize="xs" color="red.600">
                                {formatCurrency(property.outstandingBalance)} due
                              </Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(property.status)}>
                            {property.status}
                          </Badge>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<MdMoreVert />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<MdVisibility />}
                                onClick={() => navigate(`/admin/properties/${property.id}`)}
                              >
                                View Details
                              </MenuItem>
                              <MenuItem
                                icon={<MdPeople />}
                                onClick={() => navigate(`/admin/users?landlordId=${property.landlordId}`)}
                              >
                                View Landlord
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

// Mock Data (remove when API is ready)
const mockProperties: Property[] = [
  {
    id: '1',
    landlordId: 'LL001',
    landlordName: 'John Smith',
    landlordEmail: 'john.smith@example.com',
    name: 'Maple Grove Apartments',
    address: {
      street: '123 Main St',
      city: 'Toronto',
      province: 'ON',
      postalCode: 'M5V 2T6',
      country: 'Canada',
    },
    type: 'residential',
    totalUnits: 24,
    occupiedUnits: 22,
    vacantUnits: 2,
    status: 'active',
    totalMonthlyRent: 48000,
    currency: 'CAD',
    averageRentPerUnit: 2000,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2025-11-20'),
    totalTenants: 22,
    activeTenants: 22,
    collectionRate: 95.5,
    outstandingBalance: 2160,
  },
  {
    id: '2',
    landlordId: 'LL002',
    landlordName: 'Sarah Johnson',
    landlordEmail: 'sarah.j@example.com',
    name: 'Downtown Plaza',
    address: {
      street: '456 King St',
      city: 'Montreal',
      province: 'QC',
      postalCode: 'H3B 1R2',
      country: 'Canada',
    },
    type: 'commercial',
    totalUnits: 12,
    occupiedUnits: 10,
    vacantUnits: 2,
    status: 'active',
    totalMonthlyRent: 36000,
    currency: 'CAD',
    averageRentPerUnit: 3000,
    createdAt: new Date('2024-03-10'),
    updatedAt: new Date('2025-11-22'),
    totalTenants: 10,
    activeTenants: 10,
    collectionRate: 88.3,
    outstandingBalance: 4200,
  },
];

const mockStats: PropertyStats = {
  totalProperties: 2,
  activeProperties: 2,
  totalUnits: 36,
  occupancyRate: 88.9,
  totalMonthlyRevenue: 84000,
  averageCollectionRate: 91.9,
  byStatus: { active: 2, inactive: 0, pending: 0, archived: 0 },
  byType: { residential: 1, commercial: 1, mixed: 0 },
  byProvince: { ON: 1, QC: 1 },
  topLandlords: [
    { landlordId: 'LL001', landlordName: 'John Smith', propertyCount: 1, totalUnits: 24 },
    { landlordId: 'LL002', landlordName: 'Sarah Johnson', propertyCount: 1, totalUnits: 12 },
  ],
};
